import ArrowLeftIcon from "@/assets/icons/arrow-left.svg?react";
import ArrowRightIcon from "@/assets/icons/arrow-right.svg?react";
import notFound from "@/assets/images/no_image.svg";
import { BrandCarouselItem } from "@/common/lib/types";
import { CLink } from "@/components/common/customLink/CustomLink";
import Button from "@/components/ui/button/Button";
import { Carousel, Slide } from "@/components/ui/carousel/Carousel";
import BrandSingleCard from "./BrandSingleCard";
import BrandsDoubleCard from "./BrandsDoubleCard";

type BrandCardType = "single" | "double";
interface IProps {
  brandsData: BrandCarouselItem[];
  title?: string;
  navigationEnabled?: boolean;
  showViewAll?: boolean;
  lang: "en" | "ar";
  desktopViewMode?: BrandCardType;
  mobileViewMode?: BrandCardType;
}

const BrandCarousel = async ({
  brandsData,
  title,
  navigationEnabled,
  showViewAll=true,
  lang,
  desktopViewMode,
  mobileViewMode,
}: IProps) => {
  const imageData = Object.values(brandsData ?? {});

  // Helper function to create slides based on view mode
  const createSlides = (viewMode: BrandCardType | undefined, keyPrefix: string): Slide[] => {
    if (viewMode === "single") {
      // Single card mode: one brand per slide
      return brandsData?.map((item, idx) => ({
        slideClasses: "basis-auto",
        content: (
          <BrandSingleCard
            key={item.id || `${keyPrefix}-${idx}`}
            image={item.media || notFound}
            link={item.link?.url ?? "/"}
            altText={item?.slug || "logo"}
          />
        ),
      })) || [];
    }

    // Double card mode: create pairs with proper looping
    if (!brandsData || brandsData.length === 0) return [];
    
    const totalBrands = brandsData.length;
    const isOdd = totalBrands % 2 !== 0;
    const slides: Slide[] = [];
    
    if (isOdd) {
      // For odd numbers: create pairs [1,2], [3,4], [5,6], [7,8], [9,1], [2,3], [4,5], [6,7], [8,9]
      // Total slides = totalBrands (each brand appears in exactly 2 slides)
      for (let i = 0; i < totalBrands; i++) {
        const firstIndex = (i * 2) % totalBrands;
        const secondIndex = (i * 2 + 1) % totalBrands;
        
        slides.push({
          slideClasses: "basis-auto",
          content: (
            <BrandsDoubleCard
              key={`${keyPrefix}-${i}`}
              data={[brandsData[firstIndex], brandsData[secondIndex]]}
              altText={[brandsData[firstIndex]?.slug, brandsData[secondIndex]?.slug]}
            />
          ),
        });
      }
    } else {
      // For even numbers: create pairs [1,2], [3,4], [5,6], [7,8], [9,10]
      // Total slides = totalBrands / 2
      const pairsCount = totalBrands / 2;
      
      for (let i = 0; i < pairsCount; i++) {
        const firstIndex = i * 2;
        const secondIndex = i * 2 + 1;
        
        slides.push({
          slideClasses: "basis-auto",
          content: (
            <BrandsDoubleCard
              key={`${keyPrefix}-${i}`}
              data={[brandsData[firstIndex], brandsData[secondIndex]]}
              altText={[brandsData[firstIndex]?.slug, brandsData[secondIndex]?.slug]}
            />
          ),
        });
      }
    }
    
    return slides;
  };

  return (
    <section className="container px-4 md:px-12">
      <h2 className="pb-3 text-center text-base font-medium md:pb-5 md:text-xl">
        {title ? title : "Top Brands"}
      </h2>

      {/* Desktop view (lg and above): 6 logos */}
      <div className="hidden lg:block">
        <Carousel
          autoPlay={{
            stopOnMouseEnter: true,
            stopOnInteraction: false,
          }}
          carouselOptions={{
            loop: true,
            dragFree: true,
            active: navigationEnabled,
            align: "start",
          }}
          spacing={66}
          slides={brandsData?.map(
            (item, idx) =>
              ({
                slideClasses: "basis-[calc(16.666667%_-_55px)]",
                content:
                  desktopViewMode === "single" ? (
                    <BrandSingleCard
                      key={item.id || `brands-${idx}`}
                      image={item.media || notFound}
                      link={item.link?.url ?? "/"}
                      altText={item?.slug || "logo"}
                    />
                  ) : (
                    <BrandsDoubleCard
                      key={item.id || `brands-${idx}`}
                      data={[
                        brandsData[idx] || notFound,
                        brandsData[brandsData.length - idx - 1] || notFound,
                      ]}
                      altText={[
                        brandsData[idx]?.slug,
                        brandsData[brandsData.length - idx - 1]?.slug,
                      ]}
                    />
                  ),
              }) as Slide,
          )}
          controls={{
            distance: "-32px",
            left: {
              content: navigationEnabled ? (
                <div className="hidden p-2 lg:block">
                  <ArrowLeftIcon />
                </div>
              ) : (
                ""
              ),
            },
            right: {
              content: navigationEnabled ? (
                <div className="hidden p-2 lg:block">
                  <ArrowRightIcon />
                </div>
              ) : (
                ""
              ),
            },
          }}
        />
      </div>

      {/* Tablet view (md to lg): 4 logos */}
      <div className="hidden md:block lg:hidden">
        <Carousel
          autoPlay={{
            stopOnMouseEnter: true,
            stopOnInteraction: false,
          }}
          carouselOptions={{
            loop: true,
            dragFree: true,
            active: navigationEnabled,
            align: "start",
          }}
          spacing={40}
          slides={brandsData?.map(
            (item, idx) =>
              ({
                slideClasses: "basis-[calc(25%_-_30px)]",
                content: (
                  <BrandSingleCard
                    key={item.id || `brands-tablet-${idx}`}
                    image={item.media || notFound}
                    link={item.link?.url ?? "/"}
                    altText={item?.slug || "logo"}
                  />
                ),
              }) as Slide,
          )}
          controls={{
            distance: "-32px",
            left: {
              content: navigationEnabled ? (
                <div className="p-2">
                  <ArrowLeftIcon />
                </div>
              ) : (
                ""
              ),
            },
            right: {
              content: navigationEnabled ? (
                <div className="p-2">
                  <ArrowRightIcon />
                </div>
              ) : (
                ""
              ),
            },
          }}
        />
      </div>

      {/* Mobile view (below md): double card layout */}
      <div className="block overflow-hidden md:hidden">
        <Carousel
          autoPlay={{
            stopOnMouseEnter: true,
            stopOnInteraction: false,
          }}
          carouselOptions={{
            align: "center",
            loop: true,
            dragFree: true,
          }}
          spacing={35}
          slides={createSlides(mobileViewMode, "brands-mobile")}
        />
      </div>
      {showViewAll && (
      <CLink
        href="/brands"
        className="mx-auto flex w-full max-w-[10.625rem] items-center justify-center pt-7 md:pt-9"
      >
        <Button
          variant="contained"
          className="flex h-[2.5rem] w-full items-center justify-center text-sm font-semibold md:h-[3.125rem]"
        >
          {lang === "en" ? "View All" : "عرض الكل"}
        </Button>
      </CLink>
      )}
    </section>
  );
};

export default BrandCarousel;
